// Meeting links: finding one, and refusing everything else.
//   node --test test/room-links.test.mjs
//
// This runs on a screen in a room where people walk in and press the big blue
// button without reading it. Anything this function returns WILL be opened, so
// the question it answers is not "is this a link" but "is this a link we are
// willing to put a stranger's laptop in front of".
//
// ALLOWLIST, never a blocklist. A host has to be one of four, over https, with
// the host matched from the RIGHT-HAND END — "meet.google.com.evil.com" ends in
// evil.com and is not Google, which a naive includes() cannot tell.

const HOSTS = [
  'meet.google.com',
  'teams.microsoft.com',
  'teams.live.com',
  'zoom.us',            // and any subdomain: aspconsulting.zoom.us
];

/** Is this a URL we would open? A string in, a boolean out, no exceptions. */
export function allowedMeetingUrl(raw) {
  const u = parse(raw);
  if (!u) return false;
  return HOSTS.some((h) => u.hostname === h || u.hostname.endsWith(`.${h}`));
}

function parse(raw) {
  const s = String(raw == null ? '' : raw).trim();
  if (!s) return null;
  let u;
  try { u = new URL(s); } catch (e) { return null; }
  // https only. javascript:, data:, and the app schemes (msteams:, zoommtg:)
  // are all refused here — the brief forbids app schemes outright, and this
  // device deliberately has no meeting apps installed.
  if (u.protocol !== 'https:') return null;
  return u;
}

/** Which badge to show. Never guessed from the title — only from the host. */
export function platformOf(raw) {
  const u = parse(raw);
  if (!u) return null;
  if (u.hostname === 'meet.google.com') return 'Meet';
  if (u.hostname.endsWith('teams.microsoft.com') || u.hostname.endsWith('teams.live.com')) return 'Teams';
  if (u.hostname === 'zoom.us' || u.hostname.endsWith('.zoom.us')) return 'Zoom';
  return null;
}

/**
 * The web client form of a Zoom link, so the call opens in Safari rather than
 * bouncing at an app that is not installed.
 *
 * Only for a plain /j/{id} join link: the id must be digits and the password is
 * carried across untouched. Anything unexpected — a personal link, a webinar, a
 * shape not seen before — is passed through as it came, because a link that
 * opens somewhere slightly wrong is worse than one that opens where it said.
 */
export function zoomWebClient(raw) {
  const u = parse(raw);
  if (!u || !(u.hostname === 'zoom.us' || u.hostname.endsWith('.zoom.us'))) return raw;
  const m = u.pathname.match(/^\/j\/(\d+)\/?$/);
  if (!m) return raw;
  const pwd = u.searchParams.get('pwd');
  return `https://${u.hostname}/wc/join/${m[1]}${pwd ? `?pwd=${encodeURIComponent(pwd)}` : ''}`;
}

/** Every https URL in a blob of text, in the order they appear. */
function urlsIn(text) {
  const out = [];
  String(text == null ? '' : text).replace(/https:\/\/[^\s<>"')\]]+/gi, (m) => {
    // Trailing punctuation belongs to the sentence, not the URL.
    out.push(m.replace(/[.,;:!?]+$/, ''));
    return m;
  });
  return out;
}

/**
 * The join link for one calendar event, in the order the brief sets out:
 * conferenceData, then hangoutLink, then location, then description.
 *
 * The description is treated as PLAIN TEXT throughout — it is never parsed as
 * HTML and never rendered, only scanned for a URL.
 */
export function joinUrlFor(event = {}) {
  const points = ((event.conferenceData || {}).entryPoints || [])
    .filter((p) => p && p.entryPointType === 'video')
    .map((p) => p.uri);

  for (const candidate of [...points, event.hangoutLink,
    ...urlsIn(event.location), ...urlsIn(event.description)]) {
    if (allowedMeetingUrl(candidate)) return zoomWebClient(candidate);
  }
  return null;
}
